/**
 * La regla 1 del proyecto, hecha código: cada pantalla tiene DOS implementaciones y acá se
 * decide cuál se monta.
 *
 * La detección es por **viewport + tipo de puntero**, nunca por user-agent. Un user-agent
 * miente: los navegadores de escritorio se hacen pasar por móviles, los móviles piden "sitio
 * de escritorio", y cada año aparece un dispositivo nuevo que rompe la lista de reglas. El
 * ancho y el tipo de puntero describen lo que realmente importa: cuánto espacio hay y si el
 * usuario apunta con un dedo o con un mouse.
 *
 * Y hay override manual persistido, que no es un lujo: sirve para revisar la vista de gimnasio
 * desde el PC sin sacar el teléfono, y para forzar la vista densa en una tablet apoyada en el
 * rack.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Variante = 'mobile' | 'desktop';
export type Preferencia = Variante | 'auto';

const CLAVE = 'mg.layout';

/** Por debajo de esto siempre mobile: no hay ancho para una vista densa. */
const ANCHO_MOBILE = 900;
/** Con dedo, hasta este ancho conviene la vista de gimnasio (tablets incluidas). */
const ANCHO_TACTIL = 1200;

export function detectarVariante(): Variante {
  if (typeof window === 'undefined') return 'desktop';

  const ancho = window.innerWidth;
  if (ancho < ANCHO_MOBILE) return 'mobile';

  const dedo = window.matchMedia('(pointer: coarse)').matches;
  if (dedo && ancho < ANCHO_TACTIL) return 'mobile';

  return 'desktop';
}

function leerPreferencia(): Preferencia {
  try {
    const guardada = localStorage.getItem(CLAVE);
    return guardada === 'mobile' || guardada === 'desktop' ? guardada : 'auto';
  } catch {
    return 'auto';
  }
}

interface Contexto {
  variante: Variante;
  preferencia: Preferencia;
  /** null = automático. */
  fijar: (p: Preferencia) => void;
  /** La que se detectaría si no hubiera override. Para mostrarlo en Ajustes. */
  detectada: Variante;
}

const Ctx = createContext<Contexto | null>(null);

export function ProveedorVariante({ children }: { children: ReactNode }): ReactNode {
  const [detectada, setDetectada] = useState<Variante>(detectarVariante);
  const [preferencia, setPreferencia] = useState<Preferencia>(leerPreferencia);

  useEffect(() => {
    const recalcular = () => setDetectada(detectarVariante());

    // resize cubre rotar el teléfono y redimensionar la ventana. El change del media query
    // cubre conectar un mouse a una tablet, que cambia el tipo de puntero sin cambiar el ancho.
    window.addEventListener('resize', recalcular);
    const mqPuntero = window.matchMedia('(pointer: coarse)');
    mqPuntero.addEventListener('change', recalcular);

    return () => {
      window.removeEventListener('resize', recalcular);
      mqPuntero.removeEventListener('change', recalcular);
    };
  }, []);

  const fijar = useCallback((p: Preferencia) => {
    setPreferencia(p);
    try {
      if (p === 'auto') localStorage.removeItem(CLAVE);
      else localStorage.setItem(CLAVE, p);
    } catch {
      // Modo privado sin localStorage: el override vale para esta sesión y nada más.
    }
  }, []);

  const valor = useMemo<Contexto>(
    () => ({
      variante: preferencia === 'auto' ? detectada : preferencia,
      preferencia,
      fijar,
      detectada,
    }),
    [preferencia, detectada, fijar],
  );

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useVariante(): Contexto {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useVariante necesita estar dentro de <ProveedorVariante>.');
  return ctx;
}

/**
 * Helper para el archivo puente de cada pantalla. El patrón, y no hay excepciones:
 *
 *   export default elegir(InicioMobile, InicioDesktop)
 *
 * Si alguna vez las dos ramas apuntan al mismo componente, esa pantalla incumple la regla 1.
 */
export function elegir<P extends object>(
  Mobile: (props: P) => ReactNode,
  Desktop: (props: P) => ReactNode,
): (props: P) => ReactNode {
  return function Elegida(props: P) {
    const { variante } = useVariante();
    return variante === 'mobile' ? Mobile(props) : Desktop(props);
  };
}
